import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Leaf, BarChart3, Camera, DollarSign, MessageSquare,
    ChevronRight, Check, ArrowRight, Play, Users, Zap,
    CloudRain, Thermometer, Droplet, Menu, X, LogIn
} from 'lucide-react'
import RegisterForm from '../components/auth/RegisterForm'
import './LandingPage.css'

function LandingPage() {
    const [showRegister, setShowRegister] = useState(false)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const navigate = useNavigate()

    const features = [
        {
            icon: <Leaf />,
            title: 'Monitoreo de Suelo IoT',
            description: 'Sensores en tiempo real para N, P, K, pH, humedad y más. Alertas automáticas y recomendaciones de fertilización.',
            color: '#10B981'
        },
        {
            icon: <CloudRain />,
            title: 'Análisis Ambiental',
            description: 'Predicción meteorológica, cálculo de GDD y alertas de riesgo para heladas, sequías o exceso de humedad.',
            color: '#0EA5E9'
        },
        {
            icon: <Camera />,
            title: 'Visión Artificial con IA',
            description: 'Sube fotos de plantas o insectos y obtén diagnóstico instantáneo de plagas, enfermedades y plan de acción.',
            color: '#8B5CF6'
        },
        {
            icon: <BarChart3 />,
            title: 'Control Productivo',
            description: 'Trazabilidad completa desde la semilla hasta el mercado. Métricas de rendimiento por planta, m² y hectárea.',
            color: '#F59E0B'
        },
        {
            icon: <DollarSign />,
            title: 'Inteligencia Financiera',
            description: 'ROI, flujo de caja, punto de equilibrio y simulador de business case para nuevos proyectos agrícolas.',
            color: '#EF4444'
        },
        {
            icon: <MessageSquare />,
            title: 'Agente IA Conversacional',
            description: 'Pregunta en español sobre tu cultivo y recibe respuestas contextualizadas para agricultura colombiana.',
            color: '#EC4899'
        }
    ]

    const crops = ['Arroz', 'Arándanos', 'Maíz', 'Tomate', 'Gulupa']

    return (
        <div className="landing-page">
            {/* Header */}
            <header className="landing-header">
                <div className="container">
                    <div className="header-content">
                        <div className="logo-container">
                            <Leaf size={28} className="logo-icon" />
                            <span className="logo-text">TerraMind</span>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="header-nav desktop-nav">
                            <a href="#features">Características</a>
                            <a href="#cultivos">Cultivos</a>
                            <a href="#demo">Demo</a>
                            <button
                                className="btn btn-outline"
                                onClick={() => navigate('/login')}
                            >
                                Iniciar Sesión
                            </button>
                        </nav>

                        {/* Mobile Actions */}
                        <div className="mobile-header-actions">
                            <button
                                className="btn btn-primary mobile-login-btn"
                                onClick={() => navigate('/login')}
                            >
                                <LogIn size={18} />
                                <span>Ingresar</span>
                            </button>
                            <button
                                className="mobile-menu-toggle"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            >
                                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <nav className="mobile-nav">
                            <a href="#features" onClick={() => setMobileMenuOpen(false)}>Características</a>
                            <a href="#cultivos" onClick={() => setMobileMenuOpen(false)}>Cultivos</a>
                            <a href="#demo" onClick={() => setMobileMenuOpen(false)}>Demo</a>
                            <button
                                className="btn btn-primary btn-block mobile-nav-login"
                                onClick={() => navigate('/login')}
                            >
                                <LogIn size={18} />
                                Iniciar Sesión
                            </button>
                        </nav>
                    )}
                </div>
            </header>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-bg">
                    <div className="hero-gradient"></div>
                    <div className="hero-particles"></div>
                </div>
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text">
                            <div className="hero-badge">
                                <Zap size={16} />
                                <span>Potenciado por Gemini 2.0 Flash</span>
                            </div>
                            <h1>
                                Agricultura Inteligente
                                <span className="text-gradient"> para Colombia</span>
                            </h1>
                            <p className="hero-description">
                                Monitorea tus cultivos con IoT, analiza imágenes con IA,
                                optimiza tu producción y maximiza tus ganancias con TerraMind.
                            </p>
                            <div className="hero-actions">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={() => setShowRegister(true)}
                                >
                                    <span>Comenzar Gratis</span>
                                    <ArrowRight size={20} />
                                </button>
                                <a href="#demo" className="btn btn-secondary btn-lg">
                                    <Play size={20} />
                                    <span>Ver Demo</span>
                                </a>
                            </div>
                            <div className="hero-stats">
                                <div className="stat">
                                    <span className="stat-value">5+</span>
                                    <span className="stat-label">Cultivos Soportados</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-value">24/7</span>
                                    <span className="stat-label">Monitoreo Continuo</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-value">IA</span>
                                    <span className="stat-label">Análisis Avanzado</span>
                                </div>
                            </div>
                        </div>
                        <div className="hero-image">
                            <div className="hero-image-container">
                                <img
                                    src="/im1.png"
                                    alt="Dashboard de agricultura inteligente TerraMind mostrando monitoreo de cultivos en tiempo real"
                                    className="hero-img"
                                />
                                <div className="hero-image-glow"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Crops Section */}
            <section id="cultivos" className="crops-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Cultivos <span className="text-gradient">Soportados</span></h2>
                        <p>Rangos óptimos personalizados para cada tipo de cultivo colombiano</p>
                    </div>
                    <div className="crops-grid">
                        {crops.map((crop, index) => (
                            <div key={index} className="crop-card glass-card">
                                <div className="crop-icon">🌱</div>
                                <span>{crop}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Todo lo que necesitas para tu <span className="text-gradient">Cultivo</span></h2>
                        <p>Plataforma completa de agricultura de precisión con inteligencia artificial</p>
                    </div>
                    <div className="features-grid">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="feature-card glass-card"
                                style={{ '--feature-color': feature.color }}
                            >
                                <div className="feature-icon" style={{ color: feature.color }}>
                                    {feature.icon}
                                </div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                                <div className="feature-arrow">
                                    <ChevronRight size={20} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Video Demo Section */}
            <section id="demo" className="demo-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Mira TerraMind <span className="text-gradient">en Acción</span></h2>
                        <p>Descubre cómo la tecnología puede transformar tu producción agrícola</p>
                    </div>
                    <div className="video-container glass-card">
                        <iframe
                            width="100%"
                            height="500"
                            src="https://www.youtube.com/embed/scCRQwJ0-Q0"
                            title="TerraMind - Plataforma de Agricultura Inteligente"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content glass-card">
                        <div className="cta-text">
                            <h2>¿Listo para revolucionar tu agricultura?</h2>
                            <p>Únete a TerraMind y lleva tu producción al siguiente nivel con tecnología de punta.</p>
                        </div>
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={() => setShowRegister(true)}
                        >
                            <span>Registrarme Ahora</span>
                            <ArrowRight size={20} />
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <div className="logo-container">
                                <Leaf size={20} className="logo-icon-sm" />
                                <span>TerraMind</span>
                            </div>
                            <p>Agricultura inteligente para Colombia</p>
                        </div>
                        <div className="footer-links">
                            <a href="#features">Características</a>
                            <a href="#cultivos">Cultivos</a>
                            <a href="#demo">Demo</a>
                        </div>
                        <div className="footer-admin">
                            <button
                                className="admin-link"
                                onClick={() => navigate('/admin')}
                            >
                                Admin
                            </button>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© 2024 TerraMind by Saineco. Todos los derechos reservados.</p>
                    </div>
                </div>
            </footer>

            {/* Register Modal */}
            {showRegister && (
                <RegisterForm onClose={() => setShowRegister(false)} />
            )}
        </div>
    )
}

export default LandingPage
